import React from 'react'

function App() {
  const portalBaseUrl = import.meta.env.VITE_PORTAL_BASE_URL || 'http://localhost:5173';
  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 py-5 bg-gray-950/95 backdrop-blur-sm border-b border-white/10">
        <nav className="max-w-7xl mx-auto px-5 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 gradient-bg rounded-lg flex items-center justify-center font-bold text-lg">
              AI
            </div>
            <div className="text-2xl font-bold gradient-text">
              qipi.AI
            </div>
          </div>
          <a href={portalBaseUrl} className="gradient-bg text-white px-6 py-3 rounded-lg font-semibold transition-transform hover:-translate-y-0.5">
            Get Started
          </a>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center text-center relative px-5 pt-24">
        <div className="absolute inset-0 bg-gradient-radial from-purple-500/10 via-blue-500/5 to-transparent opacity-50"></div>
        <div className="max-w-4xl mx-auto relative z-10">
          <h1 className="text-6xl md:text-7xl font-extrabold mb-5 leading-tight">
            Your AI Assistant<br />
            <span className="gradient-text">Always There</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-400 mb-10 font-normal">
            Meet your personal AI companion that learns about you, helps with daily tasks, and stays connected via WhatsApp or SMS
          </p>
          <div className="flex gap-5 justify-center flex-wrap mb-10">
            <a href={portalBaseUrl} className="btn-primary">
              Get Started for Free
              <span>→</span>
            </a>
            <a href="#features" className="btn-secondary">
              See How It Works
            </a>
          </div>
          <div className="flex items-center justify-center gap-8 flex-wrap text-gray-500 text-sm">
            <span className="flex items-center gap-1">✓ No credit card required</span>
            <span className="flex items-center gap-1">✓ 5-minute setup</span>
            <span className="flex items-center gap-1">✓ Cancel anytime</span>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-32 px-5 max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4">What Your AI Can Do</h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            A powerful assistant that grows smarter with every interaction
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            {
              icon: "📅",
              title: "Smart Scheduling",
              description: "Manage appointments, set reminders, and never miss important dates"
            },
            {
              icon: "💬",
              title: "Natural Conversations",
              description: "Chat naturally via WhatsApp or SMS like talking to a friend"
            },
            {
              icon: "🧠",
              title: "Learns About You",
              description: "Adapts to your preferences, habits, and communication style"
            },
            {
              icon: "🕐",
              title: "24/7 Availability",
              description: "Always ready to help, any time of day or night"
            },
            {
              icon: "📝",
              title: "Task Management",
              description: "Track to-dos, shopping lists, and daily tasks effortlessly"
            },
            {
              icon: "💡",
              title: "Proactive Suggestions",
              description: "Get helpful reminders and suggestions based on your routine"
            }
          ].map((feature, index) => (
            <div key={index} className="bg-white/5 border border-white/10 rounded-2xl p-10 card-hover relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-0.5 gradient-bg opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>
              <div className="w-15 h-15 gradient-bg rounded-xl flex items-center justify-center mb-6 text-2xl">
                {feature.icon}
              </div>
              <h3 className="text-2xl font-semibold mb-4">{feature.title}</h3>
              <p className="text-gray-400 leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Examples Section */}
      <section className="py-32 px-5 bg-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Real-World Examples</h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              See how qipi.AI fits seamlessly into your daily life
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                number: "1",
                title: "Morning Routine",
                description: "Get your day started with a personalized morning briefing - weather, calendar, and task priorities"
              },
              {
                number: "2",
                title: "Shopping Assistant",
                description: "Add items to your list on-the-go and get recipe suggestions based on what you have"
              },
              {
                number: "3",
                title: "Meeting Prep",
                description: "Receive timely reminders with context about upcoming meetings and relevant notes"
              }
            ].map((example, index) => (
              <div key={index} className="bg-white/5 border border-white/10 rounded-2xl p-8 relative">
                <div className="absolute -top-4 left-8 w-8 h-8 gradient-bg rounded-full flex items-center justify-center font-semibold text-sm">
                  {example.number}
                </div>
                <h3 className="text-xl font-semibold mb-3 mt-2">{example.title}</h3>
                <p className="text-gray-400 leading-relaxed">{example.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Get Started Section */}
      <section id="get-started" className="py-32 px-5 text-center relative">
        <div className="absolute inset-0 bg-gradient-radial from-purple-500/5 via-blue-500/3 to-transparent"></div>
        <div className="max-w-4xl mx-auto relative z-10">
          <div className="mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">Get Started in Minutes</h2>
            <p className="text-xl text-gray-400">Three simple steps to your personal AI assistant</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-16">
            {[
              {
                number: "1",
                icon: "👤",
                title: "Sign Up",
                description: "Create your account in seconds – no credit card required"
              },
              {
                number: "2",
                icon: "✨",
                title: "Generate Your Assistant",
                description: "Customize your AI's personality and preferences to match your style"
              },
              {
                number: "3",
                icon: "💬",
                title: "Start Chatting",
                description: "Connect via WhatsApp or SMS and start getting help immediately"
              }
            ].map((step, index) => (
              <div key={index} className="relative p-10">
                <div className="absolute -top-4 right-5 w-8 h-8 gradient-bg rounded-full flex items-center justify-center font-semibold text-sm">
                  {step.number}
                </div>
                <div className="w-20 h-20 gradient-bg rounded-2xl flex items-center justify-center mx-auto mb-6 text-3xl">
                  {step.icon}
                </div>
                <h3 className="text-2xl font-semibold mb-4">{step.title}</h3>
                <p className="text-gray-400 leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 px-5 text-center bg-white/5">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-5">Ready to Meet Your AI Assistant?</h2>
          <p className="text-xl text-gray-400 mb-10">
            Join thousands of users who are already saving time and staying organized with their personal AI companion
          </p>
          <div className="flex justify-center gap-5 mb-10 flex-wrap">
            <div className="text-gray-500 text-sm flex items-center gap-1">✓ No credit card required</div>
            <div className="text-gray-500 text-sm flex items-center gap-1">✓ 5-minute setup</div>
            <div className="text-gray-500 text-sm flex items-center gap-1">✓ Cancel anytime</div>
          </div>
          <a href={portalBaseUrl} className="btn-primary">
            Get Started for Free
            <span>→</span>
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-5 text-center border-t border-white/10 text-gray-500 text-sm">
        <p>© 2025 qipi.AI - Your Personal AI Assistant</p>
      </footer>
    </div>
  )
}

export default App
